import type { MessageDescriptor } from '@lingui/core'
import { msg } from '@lingui/core/macro'
import { NAV_ITEMS_CONFIG } from 'features/navbar/navConfig'
import { SelectedTab } from 'features/navbar/types'
import { useRolodexEntries } from 'features/rolodex'
import isNil from 'lodash/isNil'
import { useMemo } from 'react'
import type { FileRouteTypes } from 'routeTree.gen'
import { RolodexEntryType } from 'types'

/** Brand-logo key for gecko `/home`. */
export const CANTILEVER_HOME_DESTINATION = 'Cantilever Home'

export type BrandHomeLink = {
  to: FileRouteTypes['to']
  destinationKey: string
  label: MessageDescriptor
}

type HomepageConfigLike = {
  tags: { site?: string }
  data: { landing_page_redirect: string }
}

const DEFAULT_BRAND_HOME_LINK: BrandHomeLink = {
  to: '/home',
  destinationKey: CANTILEVER_HOME_DESTINATION,
  label: msg`Home`,
}

export const selectLandingPageRedirect = (
  entries: ReadonlyArray<HomepageConfigLike>,
  siteId: string | undefined,
  isLoading: boolean
): string | undefined => {
  if (isLoading) return undefined
  const orgConfig = entries.find((entry) => isNil(entry.tags.site))
  const siteConfig = entries.find((entry) => entry.tags.site === siteId)
  return (siteConfig ?? orgConfig)?.data.landing_page_redirect
}

const matchNavItemForPath = (path: string): BrandHomeLink | undefined => {
  if (path === '/documents') {
    return {
      to: '/documents',
      destinationKey: SelectedTab.DocumentsHome,
      label: msg`Home`,
    }
  }

  const entry = Object.entries(NAV_ITEMS_CONFIG).find(
    ([, item]) => item?.to === path
  )
  if (entry === undefined || entry[1] === undefined) return undefined
  return {
    to: entry[1].to,
    destinationKey: entry[0],
    label: { id: entry[0], message: entry[0] },
  }
}

export const resolveBrandHomeLink = (
  landingPageRedirect: string | undefined
): BrandHomeLink => {
  if (!landingPageRedirect || landingPageRedirect === '/home') {
    return DEFAULT_BRAND_HOME_LINK
  }

  return matchNavItemForPath(landingPageRedirect) ?? DEFAULT_BRAND_HOME_LINK
}

export const useBrandHomeLink = ({
  organizationId,
  siteId,
  enabled,
}: {
  organizationId: string | undefined
  siteId: string | undefined
  enabled: boolean
}): BrandHomeLink => {
  const {
    homepageConfigs: homepageConfigEntries,
    isLoading: isLoadingHomepageConfig,
  } = useRolodexEntries(
    enabled && organizationId
      ? {
          type: [RolodexEntryType.HOMEPAGE_CONFIG],
          tags: { organization: organizationId },
        }
      : undefined
  )

  return useMemo(() => {
    const landingPageRedirect = selectLandingPageRedirect(
      homepageConfigEntries,
      siteId,
      isLoadingHomepageConfig
    )
    return resolveBrandHomeLink(landingPageRedirect)
  }, [homepageConfigEntries, isLoadingHomepageConfig, siteId])
}
